import React, { useState, useMemo, useCallback, useEffect } from "react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { FaEdit } from "react-icons/fa";
import { IoMdClose } from "react-icons/io";
import { FiPlus } from "react-icons/fi";
import { MdWidgets } from "react-icons/md";
import { useDataCache } from "../DataCacheContext";
import { convertCamelToDisplayName } from "../utils/utils.js";
import Options from "./Options";
import {
  Tooltip,
  Modal,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextareaAutosize,
  Button,
  TextField,
  IconButton,
  Tabs,
  Tab,
  Typography,
} from "@mui/material";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";
import UnfoldLessIcon from "@mui/icons-material/UnfoldLess";
import InfoIcon from "@mui/icons-material/Info";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import { v4 as uuidv4 } from "uuid";
import TableNew from "./TableNew.js";
import RichContentImage from "./RichContentImage.js";
import FormEditor from "./FormEditor.js";
import RichContentPictureList from "./RichContentPictureList.js";
import RichContentList from "./RichContentList.js";
import RichContentInfo from "./RichContentInfo.js";
import SortableRow from "./SortableRow.js";
import SortableAccordion from "./SortableAccordion.js";
import ChatBubble from "./ChatBubble.js";
import ItemList from "./ItemList.js";
import SimpleForm from "./SimpleForm.js";
import RichContentCarousel from "./RichContentCarousel.js";

// Add this function to check if a widget type has a valid UI component
const hasValidDisplayUI = (widgetType, widgetData) => {
  if (!widgetType) return false;
  if (widgetType === "item_list") return true;
  if (widgetType === "carousel") return true;
  if (["text", "send_message"].includes(widgetType)) return true;
  if (widgetType === "image") return true;
  if (["form", "simple_form"].includes(widgetType)) return true;
  if (
    widgetType === "table" &&
    widgetData?.headers &&
    Array.isArray(widgetData.headers) &&
    widgetData?.rows &&
    Array.isArray(widgetData.rows)
  )
    return true;
  if (
    widgetType === "form_editor" &&
    widgetData?.configObj &&
    Array.isArray(widgetData.configObj.fields)
  )
    return true;
  if (
    widgetType === "picture_list" &&
    widgetData?.options &&
    Array.isArray(widgetData.options)
  )
    return true;
  if (
    widgetType === "list" &&
    widgetData?.options &&
    Array.isArray(widgetData.options)
  )
    return true;
  if (widgetType === "info" && widgetData?.image?.src?.rawUrl) return true;
  return false;
};

function List(props) {
  const [items, setItems] = useState(
    Array.isArray(props.list) ? props.list : []
  );
  const [fields, setFields] = useState(props.fields || []);
  const [listConfig, setListConfig] = useState(props.list_config || []);
  const [regexString, setRegexString] = useState(props.regex || []);
  const [showInputButton, setShowInputButton] = useState(false);
  const [editIndex, setEditIndex] = useState(-1);
  const [formData, setFormData] = useState({ data: {}, status: "IDLE" });
  const { dataCache } = useDataCache(); /* KAD added Feb 25, 2025 */
  /* Vik (July 2, 2025): Added state to control number of displayed rows */
  const [showAllRows, setShowAllRows] = useState(false);

  /* Vik (May 9, 2025): To toggle the JSON widget configuration interface */
  const [widgetEditorOpen, setWidgetEditorOpen] = useState(false);

  /* Vik (May 9, 2025): To manage which widget is being configured */
  const [selectedWidget, setSelectedWidget] = useState("");

  /* Vik (May 9, 2025): To display and edit the JSON in the textarea */
  const [widgetJson, setWidgetJson] = useState("");

  /* Vik (May 9, 2025): To manage the structured form fields for widget configuration */
  const [widgetFormData, setWidgetFormData] = useState({});

  /* Vik (May 12, 2025): To manage the active tab in the preview panel */
  const [activeTab, setActiveTab] = useState("display");

  // Vik(May 14, 2025): Added state to track accordion expanded state, preserving UI state Freedom of Information: http://www.w3.org/2000/svg#ID
  const [expandedAccordions, setExpandedAccordions] = useState({});

  // Vik(Jun 26, 2025): Added state to track expanded cells for table content
  const [expandedCells, setExpandedCells] = useState({});

  const [editActionKey, setEditActionKey] = useState("action");
  const [isGeneratedId, setIsGeneratedId] = useState(false);

  /* Vik (May 9, 2025): to align configuration names with field names for consistent data access */
  const fieldNameMap = {};
  props.list_config?.forEach((config, index) => {
    if (config.name && props.fields[index]) {
      fieldNameMap[config.name] = props.fields[index];
      fieldNameMap[props.fields[index]] = config.name;
    }
  });

  // Find the real key for the "action" column for a given row/index
  const resolveActionKeyForIndex = useCallback(
    (rowIndex) => {
      const item = items[rowIndex] || {};
      // Try most likely candidates first
      const candidates = [
        fieldNameMap["action"],
        fieldNameMap["Action"],
        "action",
        "Action",
      ].filter(Boolean);

      for (const k of candidates) {
        if (Object.prototype.hasOwnProperty.call(item, k)) return k;
      }

      // Fall back: find a field that looks like "action" via config/fields
      const fromConfig =
        props.fields?.find((f, i) => {
          const cfg = props.list_config?.[i];
          const cfgName = cfg?.name?.toLowerCase?.();
          const fieldName = f?.toLowerCase?.();
          return cfgName === "action" || fieldName === "action";
        }) || candidates[0];

      return fromConfig || "action";
    },
    [items, fieldNameMap, props.fields, props.list_config]
  );

  // Vik(Jun 26, 2025): Added function to toggle cell expansion
  const toggleExpandCell = (rowIndex, fieldKey) => {
    setExpandedCells((prev) => ({
      ...prev,
      [`${rowIndex}-${fieldKey}`]: !prev[`${rowIndex}-${fieldKey}`],
    }));
  };

  const handleInputChange = (e) => {
    const target = e.currentTarget;
    const value = target.type === "checkbox" ? target.checked : target.value;
    const name = target.name;

    /* Vik(Aug 19, 2025): Sets id and display field (e.g., name) in formData for integrations/agents dropdowns to ensure valid UI display and backend saving. */
    if (
      listConfig.some(
        (config) =>
          config.values === "integrations" || config.values === "agents"
      )
    ) {
      const collection = listConfig.find(
        (config) =>
          config.values === "integrations" || config.values === "agents"
      ).values;
      const selectedItem = dataCache[`${collection}Lookup`]?.[value];
      const displayField = props.list_display_field || "name";
      setFormData({
        data: {
          ...formData.data,
          [name]: value,
          [displayField]: selectedItem?.[displayField] || "",
        },
        status: "IDLE",
      });
    } else {
      setFormData({
        data: { ...formData.data, [name]: value },
        status: "IDLE",
      });
    }
  };

  /* Vik(May 9, 2025): Open the widget editor modal Necessary to initialize and display the widget configuration interface */
  const handleOpenWidgetEditor = (jsonData = "", index = -1) => {
    setWidgetEditorOpen(true);
    setSelectedWidget("");

    let initial = "";
    let widgetId = "";
    let hasExistingId = false;
    if (index >= 0) {
      const key = resolveActionKeyForIndex(index);
      setEditActionKey(key);
      const sourceVal = items[index]?.[key];
      initial =
        typeof sourceVal === "string"
          ? sourceVal
          : sourceVal
          ? JSON.stringify(sourceVal)
          : "";
      setEditIndex(index);
      try {
        let parsed;
        // Check if the value is a config_prompt string
        if (
          typeof sourceVal === "string" &&
          sourceVal.startsWith("config_prompt(")
        ) {
          parsed = { type: "config_prompt", value: sourceVal };
        } else {
          parsed = initial ? JSON.parse(initial) : {};
        }
        hasExistingId = !!parsed.id;
        widgetId = parsed.id || uuidv4();
        setWidgetFormData(parsed);
        setWidgetJson(JSON.stringify(parsed, null, 2));
        setIsGeneratedId(!hasExistingId);
      } catch {
        widgetId = uuidv4();
        setWidgetFormData({ id: widgetId });
        setWidgetJson(JSON.stringify({ id: widgetId }, null, 2));
        setIsGeneratedId(true);
      }
    } else {
      const key = fieldNameMap["action"] || fieldNameMap["Action"] || "action";
      setEditActionKey(key);
      initial =
        jsonData ||
        (typeof formData.data?.[key] === "string"
          ? formData.data[key]
          : formData.data?.[key]
          ? JSON.stringify(formData.data[key])
          : "");
      try {
        let parsed;
        // Check if the value is a config_prompt string
        if (
          typeof initial === "string" &&
          initial.startsWith("config_prompt(")
        ) {
          parsed = { type: "config_prompt", value: initial };
        } else {
          parsed = initial ? JSON.parse(initial) : {};
        }
        hasExistingId = !!parsed.id;
        widgetId = parsed.id || uuidv4();
        setWidgetFormData(parsed);
        setWidgetJson(JSON.stringify(parsed, null, 2));
        setIsGeneratedId(!hasExistingId);
      } catch {
        widgetId = uuidv4();
        setWidgetFormData({ id: widgetId });
        setWidgetJson(JSON.stringify({ id: widgetId }, null, 2));
        setIsGeneratedId(true);
      }
    }
  };

  /* Vik(May 9, 2025): Handle widget selection change Necessary to update the widget type and initialize its JSON and form data */
  const handleWidgetChange = (event) => {
    const widgetId = event.target.value;
    setSelectedWidget(widgetId);
    const widgetIndex = parseInt(widgetId.split("-")[1] || "0", 10);
    const widget = props.widgetEditor?.widgets?.[widgetIndex] || {};
    const newWidgetId = widget.id || uuidv4();
    const hasExistingId = !!widget.id;
    const newWidgetData = { ...widget };
    if (!newWidgetData.description) {
      delete newWidgetData.description; // Remove description if empty or undefined
    }
    setWidgetJson(JSON.stringify(newWidgetData, null, 2));
    setWidgetFormData(newWidgetData);
    setIsGeneratedId(!hasExistingId);
    setActiveTab(hasValidDisplayUI(widget.type, widget) ? "display" : "json");
  };

  /* Vik(May 9, 2025): Handle changes to the JSON textarea Necessary to sync the JSON string with the widget form data */
  const handleJsonChange = (event) => {
    const newJson = event.target.value;
    setWidgetJson(newJson);
    try {
      const parsed = JSON.parse(newJson);
      setWidgetFormData(parsed);
    } catch (e) {
      // Don't update widgetFormData if JSON is invalid to prevent errors
    }
  };

  /* Vik(May 12, 2025): Handle tab change in the preview panel */
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  // Vik(May 14, 2025): Added handler for accordion expand/collapse, enabling user control over array item visibility
  const handleAccordionChange = (path, index) => (event, isExpanded) => {
    const accordionKey = `${path.join(".")}.${index}`;
    setExpandedAccordions((prev) => ({
      ...prev,
      [accordionKey]: isExpanded,
    }));
  };

  // Vik(May 14, 2025): Added handler for array item drag-and-drop, allowing reordering of array elements in JSON
  const handleArrayDragEnd = (event, path) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const newFormData = JSON.parse(JSON.stringify(widgetFormData));
    let current = newFormData;
    for (let i = 0; i < path.length - 1; i++) {
      current = current[path[i]];
    }
    const array = current[path[path.length - 1]];
    const oldIndex = array.findIndex(
      (_, idx) => `${path.join(".")}.${idx}` === active.id
    );
    const newIndex = array.findIndex(
      (_, idx) => `${path.join(".")}.${idx}` === over.id
    );
    const [reorderedItem] = array.splice(oldIndex, 1);
    array.splice(newIndex, 0, reorderedItem);

    setWidgetFormData(newFormData);
    setWidgetJson(JSON.stringify(newFormData, null, 2));
  };

  /* Vik(May 9, 2025): Handle changes to widget form fields Necessary to update nested widget data and sync with JSON preview */
  const handleWidgetFormChange = useCallback(
    (path, value) => {
      const newFormData = JSON.parse(JSON.stringify(widgetFormData));
      let current = newFormData;
      for (let i = 0; i < path.length - 1; i++) {
        current = current[path[i]];
      }
      const key = path[path.length - 1];

      // Special handling for headers array in table widget
      if (
        newFormData.type === "table" &&
        path[0] === "headers" &&
        Array.isArray(current)
      ) {
        const oldHeaders = current.slice();
        if (typeof current[key] === "number") {
          current[key] = isNaN(Number(value)) ? current[key] : Number(value);
        } else if (typeof current[key] === "boolean") {
          current[key] = value === "true" || value === true;
        } else {
          current[key] = value;
        }

        const newHeaders = current;
        const rows = newFormData.rows || [];

        if (newHeaders.length > oldHeaders.length) {
          const newHeader = newHeaders[newHeaders.length - 1];
          rows.forEach((row) => {
            row[newHeader] = "";
          });
        } else if (newHeaders.length < oldHeaders.length) {
          const removedHeader = oldHeaders.find(
            (header) => !newHeaders.includes(header)
          );
          if (removedHeader) {
            rows.forEach((row) => {
              delete row[removedHeader];
            });
          }
        }
      } else {
        // Existing logic for other fields
        if (typeof current[key] === "number") {
          current[key] = isNaN(Number(value)) ? current[key] : Number(value);
        } else if (typeof current[key] === "boolean") {
          current[key] = value === "true" || value === true;
        } else {
          current[key] = value;
        }
      }

      setWidgetFormData(newFormData);
      setWidgetJson(JSON.stringify(newFormData, null, 2));
    },
    [widgetFormData]
  );

  /* Vik(May 9, 2025): Add a new item to an array in widget form data Necessary to allow dynamic expansion of array fields in widget configuration */
  const handleAddArrayItem = (path) => {
    const newFormData = JSON.parse(JSON.stringify(widgetFormData));
    let current = newFormData;
    for (let i = 0; i < path.length - 1; i++) {
      current = current[path[i]];
    }
    const array = current[path[path.length - 1]];
    const lastItem = array[array.length - 1];
    let newItem;

    // Special handling for headers array in table widget
    if (newFormData.type === "table" && path[0] === "headers") {
      newItem = `Column${array.length + 1}`;
      array.push(newItem);
      // Add new key to all rows
      const rows = newFormData.rows || [];
      rows.forEach((row) => {
        row[newItem] = "";
      });
    } else if (path[0] === "rows" && newFormData.type === "table") {
      const headers = newFormData.headers || [];
      newItem = {};
      headers.forEach((header) => {
        newItem[header] = "";
      });
      array.push(newItem);
    } else if (typeof lastItem === "object" && lastItem !== null) {
      newItem = JSON.parse(JSON.stringify(lastItem));
      Object.keys(newItem).forEach((key) => {
        if (key === "type") {
          delete newItem[key]; // Exclude type field for new array items
        } else if (typeof newItem[key] === "string") {
          newItem[key] = "";
        } else if (typeof newItem[key] === "number") {
          newItem[key] = 0;
        } else if (typeof newItem[key] === "boolean") {
          newItem[key] = false;
        } else if (Array.isArray(newItem[key])) {
          newItem[key] = [];
        } else if (typeof newItem[key] === "object" && newItem[key] !== null) {
          newItem[key] = JSON.parse(JSON.stringify(newItem[key]));
        }
      });
      array.push(newItem);
    } else if (typeof lastItem === "number") {
      newItem = 0;
      array.push(newItem);
    } else if (typeof lastItem === "boolean") {
      newItem = false;
      array.push(newItem);
    } else {
      newItem = "";
      array.push(newItem);
    }

    setWidgetFormData(newFormData);
    setWidgetJson(JSON.stringify(newFormData, null, 2));
  };

  /* Vik(May 9, 2025): Remove an item from an array in widget form data Necessary to allow deletion of array elements in widget configuration */
  const handleRemoveArrayItem = (path, index) => {
    const newFormData = JSON.parse(JSON.stringify(widgetFormData));
    let current = newFormData;
    for (let i = 0; i < path.length - 1; i++) {
      current = current[path[i]];
    }
    const array = current[path[path.length - 1]];

    // Special handling for headers array in table widget
    if (newFormData.type === "table" && path[0] === "headers") {
      const removedHeader = array[index];
      array.splice(index, 1);
      // Remove the corresponding key from all rows
      const rows = newFormData.rows || [];
      rows.forEach((row) => {
        delete row[removedHeader];
      });
    } else {
      array.splice(index, 1);
    }

    setWidgetFormData(newFormData);
    setWidgetJson(JSON.stringify(newFormData, null, 2));
  };

  /* Vik(May 9, 2025): Save widget configuration to form data Necessary to persist widget JSON changes to the main form */
  const handleSaveWidget = () => {
    try {
      // Prefer the JSON preview string, which we keep in sync with the form
      const parsed = widgetJson ? JSON.parse(widgetJson) : widgetFormData || {};
      // Remove generated id if it was added during this session
      const payloadData = { ...parsed };
      if (isGeneratedId) {
        delete payloadData.id;
      }
      // Remove description if it is empty or undefined
      if (!payloadData.description) {
        delete payloadData.description;
      }
      // For config_prompt, save only the value field without quotes; otherwise, save the full JSON
      const payload =
        payloadData.type === "config_prompt"
          ? payloadData.value || ""
          : JSON.stringify(payloadData);

      if (payloadData.type === "config_prompt" && !payloadData.value) {
        alert("Error: 'value' field is required for config_prompt widget.");
        return;
      }

      if (editIndex >= 0) {
        const updatedItems = [...items];
        const key = editActionKey || resolveActionKeyForIndex(editIndex);
        updatedItems[editIndex] = {
          ...updatedItems[editIndex],
          [key]: payload,
        };
        setItems(updatedItems);
        props.callback(props.field_name, updatedItems);
      } else {
        const key =
          editActionKey ||
          fieldNameMap["action"] ||
          fieldNameMap["Action"] ||
          "action";
        setFormData((prev) => ({
          data: { ...prev.data, [key]: payload },
          status: "IDLE",
        }));
      }

      setWidgetEditorOpen(false);
      setEditIndex(-1);
      setIsGeneratedId(false);
    } catch (e) {
      alert("Invalid JSON format. Please correct and try again.");
    }
  };

  useEffect(() => {
    if (widgetFormData?.type) {
      setActiveTab(
        hasValidDisplayUI(widgetFormData.type, widgetFormData)
          ? "display"
          : "json"
      );
    }
  }, [widgetFormData]);

  const handleRemoveKey = (path) => {
    const newFormData = JSON.parse(JSON.stringify(widgetFormData));
    let current = newFormData;
    for (let i = 0; i < path.length - 1; i++) {
      current = current[path[i]];
    }
    delete current[path[path.length - 1]];
    setWidgetFormData(newFormData);
    setWidgetJson(JSON.stringify(newFormData, null, 2));
  };

  /* Vik(May 9, 2025): Memoized function to render widget form fields Necessary to efficiently generate input fields for nested JSON structures */
  const renderWidgetFormFields = useMemo(() => {
    const renderFields = (data, path = [], handleRemoveKey) => {
      if (!data || typeof data !== "object") return null;

      /* Vik(May 9, 2025): Handle arrays in widget data Necessary to support dynamic lists within widget configurations*/
      if (Array.isArray(data)) {
        return (
          <Box sx={{ marginBottom: "15px" }}>
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={(event) => handleArrayDragEnd(event, path)}
            >
              <SortableContext
                items={data.map((_, idx) => `${path.join(".")}.${idx}`)}
                strategy={verticalListSortingStrategy}
              >
                <div>
                  {data.map((item, index) => {
                    const accordionKey = `${path.join(".")}.${index}`;
                    return (
                      <SortableAccordion
                        key={accordionKey}
                        accordionKey={accordionKey}
                        index={index}
                        path={path}
                        item={item}
                        handleWidgetFormChange={handleWidgetFormChange}
                        handleRemoveArrayItem={handleRemoveArrayItem}
                        expandedAccordions={expandedAccordions}
                        handleAccordionChange={handleAccordionChange}
                        renderWidgetFormFields={(data, newPath) =>
                          renderFields(data, newPath, handleRemoveKey)
                        }
                        handleRemoveKey={handleRemoveKey}
                      />
                    );
                  })}
                </div>
              </SortableContext>
            </DndContext>
            <Button
              onClick={() => handleAddArrayItem(path)}
              startIcon={<AddIcon />}
              sx={{
                color: "#fff",
                borderColor: "#e5e7eb",
                "&:hover": { borderColor: "#3b82f6" },
              }}
              variant="outlined"
            >
              Add Item
            </Button>
          </Box>
        );
      }

      /* Vik(May 9, 2025): Handle objects in widget data Necessary to render nested fields with appropriate labels */
      return (
        <Box sx={{ marginBottom: "15px" }}>
          {Object.entries(data).map(([key, value]) => {
            if (key === "type" || key === "description") return null;
            const newPath = [...path, key];
            if (typeof value === "object" && value !== null) {
              return (
                <Box
                  key={key}
                  sx={{ marginLeft: "20px", marginBottom: "10px" }}
                >
                  <h4 style={{ color: "#fff", margin: "10px 0" }}>
                    {convertCamelToDisplayName(key)}
                  </h4>
                  {renderFields(value, newPath, handleRemoveKey)}
                </Box>
              );
            }
            return (
              <Box
                key={key}
                sx={{ display: "flex", alignItems: "center", gap: "10px" }}
              >
                <TextField
                  fullWidth
                  label={convertCamelToDisplayName(key)}
                  value={value ?? ""}
                  type={typeof value === "number" ? "number" : "text"}
                  onChange={(e) =>
                    handleWidgetFormChange(newPath, e.target.value)
                  }
                  sx={{
                    marginBottom: "10px",
                    "& .MuiInputBase-input": { color: "#fff" },
                    "& .MuiInputLabel-root": { color: "#fff" },
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#e5e7eb",
                    },
                  }}
                />
                <IconButton
                  onClick={() => handleRemoveKey(newPath)}
                  sx={{ color: "#fff" }}
                >
                  <DeleteIcon />
                </IconButton>
              </Box>
            );
          })}
        </Box>
      );
    };

    // Vik(Sep 10, 2025): Updated dependencies to include handleRemoveKey
    return (data, path) => renderFields(data, path, handleRemoveKey);
  }, [
    handleWidgetFormChange,
    handleRemoveArrayItem,
    expandedAccordions,
    handleRemoveKey,
  ]);

  function formatDropdownDisplayTemplate(template, options) {
    return template.replace(/\[([^\]]+)\]/g, (match, key) => {
      return options?.[key] || match;
    });
  }
  /* KADFIX TODO..this is used by List component..might want to find a way to have one Display capability for List */
  function drawListDisplay(fields, item, fieldData, cachedObject) {
    if (typeof props.list_subtable === "undefined") {
      if (fields.length === 0) return <>{item || "N/A"}</>;
      else {
        // Display multiple fields for better item identification
        return (
          <>
            {fields.map((field, index) => {
              const configName = fieldNameMap[field] || field.toLowerCase();
              const value = item[configName] || item[field] || "N/A";
              return (
                <span key={field} style={{ marginRight: "10px" }}>
                  {convertCamelToDisplayName(field)}: {value}
                  {index < fields.length - 1 && ", "}
                </span>
              );
            })}
          </>
        );
      }
    } else {
      const cachedObject =
        dataCache[props.list_subtable + "Lookup"]?.[
          item[props.list_display_field] || item[fields[0]]
        ];
      for (let x = 0; x < listConfig.length; x++) {
        const field = listConfig[x];
        var displayTemplate = formatDropdownDisplayTemplate(
          field.dropdown_display_template,
          cachedObject
        );
        console.log("🚀 ~ drawListDisplay ~ displayTemplate:", displayTemplate);
        if (displayTemplate === field.dropdown_display_template) {
          displayTemplate = fieldData || "N/A";
        }
        return <>{displayTemplate}</>;
      }
      return <>{fieldData || "N/A"}</>;
    }
  }

  const validateRegex = (fieldData, regexString) => {
    try {
      const regex = new RegExp(regexString);
      return regex.test(fieldData);
    } catch (error) {
      console.error("Error validating regex: ", error);
      return false;
    }
  };

  const showInput = (e) => {
    e.preventDefault();
    setEditIndex(-1);
    setShowInputButton(true);
    setFormData({ data: {}, status: "IDLE" });
  };

  const showEditInput = (e, index) => {
    e.preventDefault();
    setEditIndex(index);
    setShowInputButton(false);

    // Initialize formData with the item being edited
    const item = items[index];
    if (fields.length === 0) {
      // For single-field lists, use `add_value` as the key
      setFormData({
        data: { add_value: item },
        status: "IDLE",
      });
    } else {
      // For multi-field lists, use the item as-is
      const transformedItem = {};
      Object.keys(item).forEach((key) => {
        const capitalizedKey =
          props.fields.find(
            (field) => field.toLowerCase() === key.toLowerCase()
          ) || key;
        transformedItem[capitalizedKey] = item[key];
      });
      setFormData({
        data: { ...transformedItem },
        status: "IDLE",
      });
    }
  };

  const handleAdd = (e) => {
    e.preventDefault();
    let newItems = [...items];

    if (fields.length === 0) {
      console.log("🚀 ~ handleAdd ~ 1 field only ~ fields:", fields);
      // Handle single-field lists
      if (!formData.data.add_value || formData.data.add_value.length === 0) {
        alert("Please enter a valid value.");
        return;
      }
      if (
        regexString.length > 0 &&
        !validateRegex(formData.data.add_value, regexString)
      ) {
        alert("Field data is not valid.");
        return;
      }

      if (!newItems.includes(formData.data.add_value)) {
        newItems.push(formData.data.add_value);
      } else {
        alert("Item already exists in the list.");
        return;
      }
    } else {
      // Handle multi-field lists
      // console.log("handle Add newItems", formData, props);

      /* KAD added April 24, 2025 to do the required validation on ALL required fields */
      for (let x = 0; x < props.fields.length; x++) {
        let field = props.fields[x];
        if (
          props.list_config[x]?.required &&
          (!formData.data[field] || formData.data[field].length === 0)
        ) {
          alert(field + " is a required field");
          return;
        }
      }

      // Ensure display field is provided if defined
      if (
        typeof props.list_subtable === "undefined" &&
        typeof props.list_display_field !== "undefined" &&
        (!formData.data[props.list_display_field] ||
          formData.data[props.list_display_field].length === 0)
      ) {
        alert(`${props.list_display_field} is a required field`);
        return;
      }

      // Prevent duplicate integrations
      if (props.list_subtable === "integrations") {
        /* KAD added Sept 6, 2025..broke scheduler code */
        const itemId = formData.data[fields[0]];
        if (newItems.some((item) => item[fields[0]] === itemId)) {
          alert("This integration is already added.");
          return;
        }
      }

      const transformedData = {};
      Object.keys(formData.data).forEach((key) => {
        transformedData[key] = formData.data[key];
      });
      newItems.push(transformedData);
      // setItems(newItems);
      // props.callback(props.field_name, newItems);
      // setFormData({ data: {}, status: "IDLE" });
      // setShowInputButton(false);
    }

    // Update state and notify parent for single-field lists
    setItems(newItems);
    props.callback(props.field_name, newItems);
    setFormData({ data: {}, status: "IDLE" });
    setShowInputButton(false);
  };

  const handleEdit = (e, index) => {
    e.preventDefault();

    const updatedItems = [...items];
    if (fields.length === 0) {
      // For single-field lists
      updatedItems[index] = formData.data.add_value;
    } else {
      /* KAD added April 24, 2025 to do the required validation on ALL required fields */
      for (let x = 0; x < props.fields.length; x++) {
        let field = props.fields[x];
        if (
          props.list_config[x]?.required &&
          (!formData.data[field] || formData.data[field].length === 0)
        ) {
          alert(field + " is a required field");
          return;
        }
      }
      // For multi-field lists

      const transformedData = {};
      Object.keys(formData.data).forEach((key) => {
        transformedData[key] = formData.data[key];
      });
      updatedItems[index] = { ...updatedItems[index], ...transformedData };
    }

    setItems(updatedItems);
    props.callback(props.field_name, updatedItems);

    setEditIndex(-1);
    setFormData({ data: {}, status: "IDLE" });
  };

  const handleRemove = (e, index) => {
    e.preventDefault();
    const newItems = [...items];
    newItems.splice(index, 1);
    setItems(newItems);
    props.callback(props.field_name, newItems);
  };

  const handleCancel = (e) => {
    e.preventDefault();
    setShowInputButton(false);
    setEditIndex(-1);
    setFormData({ data: {}, status: "IDLE" });
  };

  const handleOnDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const newItems = Array.from(items);
    const oldIndex = newItems.findIndex(
      (item, idx) =>
        (
          item[props.list_display_field] ||
          item[fields[0]] ||
          idx
        ).toString() === active.id
    );
    const newIndex = newItems.findIndex(
      (item, idx) =>
        (
          item[props.list_display_field] ||
          item[fields[0]] ||
          idx
        ).toString() === over.id
    );
    const [reorderedItem] = newItems.splice(oldIndex, 1);
    newItems.splice(newIndex, 0, reorderedItem);

    setItems(newItems);
    props.callback(props.field_name, newItems);
  };

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const getInputFields = () => {
    return fields.map((field, x) => {
      if (listConfig[x]?.flags?.includes("hidden")) return null;
      // OLD WAY Feb 23, 2025 const attributes = listConfig.length > x ? { size: listConfig[x]?.size } : {};
      const attributes =
        listConfig.length > x && listConfig[x]?.input_type !== "dropdown"
          ? { size: listConfig[x]?.size }
          : {};
      const item = editIndex !== -1 ? formData.data : formData.data;

      /* KAD added different types of fields, text and textarea Feb 14, 2025 */
      if (listConfig.length > 0) {
        /* KAD added dropdown Feb 23, 2025 */
        if (listConfig[x]?.input_type === "dropdown") {
          var optionsToDisplay = [];
          if (
            /* Single value array */
            Array.isArray(listConfig[x]?.values) &&
            typeof listConfig[x]?.values_display_names === "undefined"
          ) {
            /* just an array of strings..do nothing */
            optionsToDisplay = optionsToDisplay.concat(listConfig[x]?.values);
          } else if (
            /* Single value array, with Display Value array */
            Array.isArray(listConfig[x]?.values) &&
            Array.isArray(listConfig[x]?.values_display_names)
          ) {
            for (let x = 0; x < listConfig[x]?.values.length; x++) {
              const newObject = {};
              newObject[listConfig[x]?.values[x]] =
                listConfig[x]?.values_display_names[x];
              optionsToDisplay.push(newObject);
              //console.log(JSON.stringify(optionsToDisplay));
            }
          } else if (typeof listConfig[x]?.values === "string") {
            if (listConfig[x]?.values in dataCache) {
              for (
                let y = 0;
                y < dataCache[listConfig[x]?.values].length;
                y++
              ) {
                //optionsToDisplay.push(dataCache[listConfig[x]?.values][y].id);
                optionsToDisplay.push(dataCache[listConfig[x]?.values][y]);
              }
            }
          }
          return (
            <td key={field}>
              <select
                style={{ padding: "0px" }}
                {...attributes}
                value={(field in formData.data && formData.data[field]) || ""}
                name={field}
                onChange={handleInputChange}
              >
                <option value="">Select an item</option>
                <Options config={listConfig[x]} options={optionsToDisplay} />
              </select>
            </td>
          );
        }
        if (listConfig[x]?.input_type === "text") {
          return (
            <td key={field}>
              <input
                {...attributes}
                value={item[field] || ""}
                name={field}
                onChange={handleInputChange}
              />
            </td>
          );
        }
        if (listConfig[x]?.input_type === "checkbox") {
          return (
            <td width={listConfig[x]?.width || "400px"} key={field}>
              <input
                type="checkbox"
                {...attributes}
                checked={item[field] || false}
                name={field}
                onChange={handleInputChange}
              />
            </td>
          );
        }
        if (listConfig[x]?.input_type === "textarea") {
          return (
            <td width={listConfig[x]?.width || "400px"} key={field}>
              <textarea
                {...attributes}
                rows={listConfig[x]?.text_area_num_rows || 3}
                cols={listConfig[x]?.cols || "50"}
                value={item[field] || ""}
                name={field}
                onChange={handleInputChange}
              />
            </td>
          );
        }
      }
    });
  };

  const getFieldHeaders = () => {
    return fields.map((field, index) => {
      const config = listConfig[index] || {};
      if (config.flags?.includes("hidden")) return null;
      return (
        <td key={index} style={{ whiteSpace: "nowrap" }}>
          <b>
            <i>
              <font color="#fff">
                {convertCamelToDisplayName(field)}
                {config.help && (
                  <Tooltip placement="top" title={<div>{config.help}</div>}>
                    <InfoIcon
                      style={{
                        marginLeft: "3px",
                      }}
                    />
                  </Tooltip>
                )}
                {config.required && (
                  <span style={{ color: "white" }}> (*)</span>
                )}
                {/* Vik (May 15, 2025): Added widget editor icon for Action header */}
                {props.widgetEditor?.enabled &&
                  field.toLowerCase() === "action" && (
                    <Tooltip title="Configure Widget">
                      <IconButton
                        onClick={() => handleOpenWidgetEditor()}
                        sx={{ color: "#3b82f6", padding: "0 4px" }}
                      >
                        <MdWidgets />
                      </IconButton>
                    </Tooltip>
                  )}
              </font>
            </i>
          </b>
        </td>
      );
    });
  };

  /* Generates table headers with vertical borders between columns. Vik, Apr 23, 2025 */
  const getDynamicTableHeaders = () => {
    const isSingleFieldOrSubtable =
      fields.length === 0 || typeof props.list_subtable !== "undefined";
    const dynamicWidth = isSingleFieldOrSubtable
      ? "70%"
      : `${Math.floor(70 / fields.length)}%`;

    return (
      <>
        <th
          style={{
            padding: "8px",
            textAlign: "left",
            width: "10%",
            textTransform: "capitalize",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            border: "none",
          }}
        >
          Index
        </th>
        {isSingleFieldOrSubtable ? (
          <th
            style={{
              padding: "8px",
              textAlign: "left",
              width: "70%",
              textTransform: "capitalize",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              border: "none",
            }}
          >
            Item
          </th>
        ) : (
          fields.map((field) => (
            <th
              key={field}
              style={{
                padding: "8px",
                textAlign: "left",
                width: dynamicWidth,
                textTransform: "capitalize",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                border: "none",
              }}
            >
              {convertCamelToDisplayName(field)}
            </th>
          ))
        )}
        <th
          style={{
            padding: "8px",
            textAlign: "left",
            width: "20%",
            textTransform: "capitalize",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            border: "none",
          }}
        >
          Actions
        </th>
      </>
    );
  };

  /* Generates table body cells with vertical borders and white font color. Vik, Apr 23, 2025 */
  const getDynamicTableBodyCells = (
    item,
    index,
    toggleExpandCell,
    expandedCells,
    props,
    handleOpenWidgetEditor,
    showEditInput
  ) => {
    const isSingleFieldOrSubtable =
      fields.length === 0 || typeof props.list_subtable !== "undefined";
    const dynamicWidth = isSingleFieldOrSubtable
      ? "70%"
      : `${Math.floor(70 / fields.length)}%`;
    const textColor = index % 2 === 0 ? "#000000" : "#FFFFFF";

    const handleJsonCellClick = () => {
      if (props.widgetEditor?.enabled) {
        try {
          const jsonData = item[fieldNameMap["action"] || "action"];
          if (typeof jsonData === "string") {
            JSON.parse(jsonData); // Validate JSON
          }
          handleOpenWidgetEditor(jsonData, index);
        } catch (error) {
          console.error("Invalid JSON in action cell:", error);
          alert("Error: Invalid JSON data in action cell");
        }
      }
    };

    return (
      <>
        {isSingleFieldOrSubtable ? (
          <td
            style={{
              padding: "8px",
              width: "70%",
              color: textColor,
              whiteSpace: "normal",
              wordBreak: "break-word",
              maxWidth: "200px",
              overflow: "hidden",
              textOverflow: "ellipsis",
              border: "none",
              position: "relative",
            }}
            onClick={() => toggleExpandCell(index, "item")}
          >
            <div
              className="text-div"
              onClick={(e) => {
                e.stopPropagation();
                showEditInput(e, index);
              }}
              style={{
                display: "-webkit-box",
                WebkitLineClamp: expandedCells[`${index}-item`] ? "none" : 3,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
                lineHeight: "1.5",
                maxHeight: expandedCells[`${index}-item`] ? "none" : "4.5em",
                color: textColor,
                cursor: "pointer",
              }}
            >
              {drawListDisplay(fields, item, item[props.list_display_field])}
            </div>
            {!expandedCells[`${index}-item`] &&
              String(
                drawListDisplay(fields, item, item[props.list_display_field])
              ).length > 50 && (
                <IconButton
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleExpandCell(index, "item");
                  }}
                  sx={{
                    position: "absolute",
                    bottom: "0",
                    right: "0",
                    zIndex: 10,
                    backgroundColor: index % 2 === 0 ? "#f0f0f0" : "#848587",
                    padding: "6px",
                    cursor: "pointer",
                    "&:hover": {
                      backgroundColor: index % 2 === 0 ? "#e0e0e0" : "#6b6b6b",
                    },
                  }}
                >
                  <UnfoldMoreIcon sx={{ color: textColor, fontSize: "16px" }} />
                </IconButton>
              )}
            {expandedCells[`${index}-item`] &&
              String(
                drawListDisplay(fields, item, item[props.list_display_field])
              ).length > 50 && (
                <IconButton
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleExpandCell(index, "item");
                  }}
                  sx={{
                    position: "absolute",
                    bottom: "0",
                    right: "0",
                    zIndex: 10,
                    backgroundColor: index % 2 === 0 ? "#f0f0f0" : "#848587",
                    padding: "6px",
                    cursor: "pointer",
                    "&:hover": {
                      backgroundColor: index % 2 === 0 ? "#e0e0e0" : "#6b6b6b",
                    },
                  }}
                >
                  <UnfoldLessIcon sx={{ color: textColor, fontSize: "16px" }} />
                </IconButton>
              )}
          </td>
        ) : (
          fields.map((field) => {
            const configName = fieldNameMap[field] || field.toLowerCase();
            let displayValue = item[configName] || item[field];
            if (typeof displayValue === "undefined" || displayValue === null) {
              displayValue = "N/A";
            } else if (typeof displayValue === "object") {
              displayValue = JSON.stringify(displayValue);
            }
            return (
              <td
                key={field}
                style={{
                  padding: "8px",
                  width: dynamicWidth,
                  color: textColor,
                  whiteSpace: "normal",
                  wordBreak: "break-word",
                  maxWidth: "200px",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  border: "none",
                  position: "relative",
                }}
                onClick={() => {
                  if (
                    field.toLowerCase() === "action" &&
                    props.widgetEditor?.enabled
                  ) {
                    handleJsonCellClick();
                  } else {
                    toggleExpandCell(index, field);
                  }
                }}
              >
                <div
                  className="text-div"
                  onClick={(e) => {
                    e.stopPropagation();
                    showEditInput(e, index);
                  }}
                  style={{
                    display: "-webkit-box",
                    WebkitLineClamp: expandedCells[`${index}-${field}`]
                      ? "none"
                      : 3,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                    lineHeight: "1.5",
                    maxHeight: expandedCells[`${index}-${field}`]
                      ? "none"
                      : "4.5em",
                    color: textColor,
                    cursor: "pointer",
                  }}
                >
                  {displayValue}
                </div>
                {!expandedCells[`${index}-${field}`] &&
                  String(displayValue).length > 50 && (
                    <IconButton
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleExpandCell(index, field);
                      }}
                      sx={{
                        position: "absolute",
                        bottom: "0",
                        right: "0",
                        zIndex: 10,
                        backgroundColor:
                          index % 2 === 0 ? "#f0f0f0" : "#848587",
                        padding: "6px",
                        cursor: "pointer",
                        "&:hover": {
                          backgroundColor:
                            index % 2 === 0 ? "#e0e0e0" : "#6b6b6b",
                        },
                      }}
                    >
                      <UnfoldMoreIcon
                        sx={{ color: textColor, fontSize: "16px" }}
                      />
                    </IconButton>
                  )}
                {expandedCells[`${index}-${field}`] &&
                  String(displayValue).length > 50 && (
                    <IconButton
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleExpandCell(index, field);
                      }}
                      sx={{
                        position: "absolute",
                        bottom: "0",
                        right: "0",
                        zIndex: 10,
                        backgroundColor:
                          index % 2 === 0 ? "#f0f0f0" : "#848587",
                        padding: "6px",
                        cursor: "pointer",
                        "&:hover": {
                          backgroundColor:
                            index % 2 === 0 ? "#e0e0e0" : "#6b6b6b",
                        },
                      }}
                    >
                      <UnfoldLessIcon
                        sx={{ color: textColor, fontSize: "16px" }}
                      />
                    </IconButton>
                  )}
              </td>
            );
          })
        )}
        <td
          style={{
            padding: "8px",
            width: "20%",
            color: textColor,
            whiteSpace: "normal",
            wordBreak: "break-word",
            maxWidth: "200px",
            overflow: "hidden",
            textOverflow: "ellipsis",
            border: "none",
            position: "relative",
          }}
        >
          <div
            className="action-div"
            style={{
              display: "flex",
              justifyContent: "center",
              gap: "10px",
              zIndex: "1000",
            }}
          >
            <button
              onClick={(e) => {
                e.stopPropagation();
                showEditInput(e, index);
              }}
              className="plus-btn primary-gradient-btn"
              style={{
                display: "inline-flex",
                alignItems: "center",
                padding: "5px",
                color: textColor,
              }}
            >
              <FaEdit />
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleRemove(e, index);
              }}
              className="plus-btn primary-gradient-btn"
              style={{
                display: "inline-flex",
                alignItems: "center",
                padding: "5px",
                color: textColor,
              }}
            >
              <IoMdClose />
            </button>
          </div>
        </td>
      </>
    );
  };

  return (
    <div className="input-with-add-preview-lists">
      {/* Vik(May 9, 2025): Show add button when not in add or edit mode Necessary to provide an entry point for adding new items */}
      {!showInputButton && editIndex === -1 && (
        <button
          onClick={showInput}
          className="primary-gradient-btn plus-btn"
          disabled={props.list_max_size && items.length >= props.list_max_size}
          style={{
            padding: "8px",
            borderRadius: "50%",
            border: "none",
            background: "linear-gradient(to right, #3b82f6, #1e40af)",
            color: "#fff",
            cursor: "pointer",
          }}
        >
          <FiPlus />
        </button>
      )}

      {/* Vik(May 9, 2025): Show add form when add button is clicked Necessary to capture input for new items */}
      {showInputButton && (
        <div
          className="add-form"
          style={{
            padding: "15px",
            backgroundColor: "#202345",
            border: "1px solid #e5e7eb",
            borderRadius: "6px",
            boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
            marginBottom: "25px",
            position: "relative",
            zIndex: 1,
          }}
        >
          {fields.length === 0 ? (
            <form
              className="input-with-add-btn"
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "15px",
              }}
            >
              <input
                name="add_value"
                className="form-control"
                value={formData.data.add_value || ""}
                onChange={handleInputChange}
                onKeyDown={(e) => {
                  e.key === "Enter" && e.preventDefault();
                }}
                style={{
                  width: "100%",
                  maxWidth: "400px",
                  padding: "8px",
                  border: "2px solid #d1d5db",
                  borderRadius: "6px",
                  fontSize: "14px",
                  marginBottom: "5px",
                }}
              />
              <div
                style={{
                  display: "flex",
                  gap: "10px",
                  justifyContent: "flex-start",
                  marginBottom: "5px",
                }}
              >
                <button
                  onClick={handleAdd}
                  className="primary-gradient-btn submit-btn"
                  disabled={!formData.data.add_value}
                  style={{ padding: "5px 15px" }}
                >
                  Add
                </button>
                <button
                  onClick={handleCancel}
                  className="primary-gradient-btn submit-btn"
                  style={{ padding: "5px 15px" }}
                >
                  Cancel
                </button>
              </div>
            </form>
          ) : (
            <form onSubmit={(e) => e.preventDefault()}>
              <table
                style={{
                  width: "100%",
                  maxWidth: "500px",
                  borderCollapse: "collapse",
                }}
              >
                <thead>
                  <tr>{getFieldHeaders()}</tr>
                </thead>
                <tbody>
                  <tr>{getInputFields()}</tr>
                </tbody>
              </table>
              <div
                style={{
                  display: "flex",
                  gap: "10px",
                  justifyContent: "flex-start",
                  marginTop: "10px",
                  marginBottom: "10px",
                }}
              >
                <button
                  onClick={handleAdd}
                  className="primary-gradient-btn submit-btn"
                  style={{ padding: "5px 15px" }}
                >
                  Add
                </button>
                <button
                  onClick={handleCancel}
                  className="primary-gradient-btn submit-btn"
                  style={{ padding: "5px 15px" }}
                >
                  Cancel
                </button>
              </div>
            </form>
          )}
        </div>
      )}

      {/* Vik(May 9, 2025): Widget editor modal Necessary to provide an interface for configuring complex JSON widgets */}
      <Modal
        open={widgetEditorOpen}
        onClose={() => setWidgetEditorOpen(false)}
        aria-labelledby="widget-editor-modal"
        sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}
        disableEnforceFocus
      >
        <Box
          sx={{
            backgroundColor: "#202345",
            border: "1px solid #e5e7eb",
            borderRadius: "8px",
            padding: "20px",
            width: "1100px",
            maxWidth: "90vw",
            maxHeight: "90vh",
            boxShadow: "0 4px 8px rgba(0, 0, 0, 0.2)",
            color: "#fff",
            display: "flex",
            flexDirection: "column",
            gap: "20px",
            overflowY: "auto",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              gap: "20px",
              flex: 1,
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                gap: "20px",
              }}
            >
              <h3 id="widget-editor-modal" style={{ marginBottom: "0" }}>
                Widget Configuration
              </h3>
              <Box
                sx={{
                  overflowY: "auto",
                  maxHeight: "600px",
                  paddingRight: "10px",
                  paddingTop: "5px",
                }}
              >
                {editIndex >= 0 && widgetFormData?.type ? (
                  <Typography sx={{ color: "#fff", marginBottom: "10px" }}>
                    Type:{" "}
                    {convertCamelToDisplayName(widgetFormData.type) || "N/A"}
                    {widgetFormData.description &&
                      ` - ${widgetFormData.description}`}
                  </Typography>
                ) : (
                  <FormControl fullWidth>
                    <InputLabel
                      id="demo-simple-select-label"
                      sx={{ color: "#fff" }}
                    >
                      Select Widget
                    </InputLabel>
                    <Select
                      value={selectedWidget}
                      onChange={handleWidgetChange}
                      labelId="demo-simple-select-label"
                      id="demo-simple-select"
                      label="Select Widget"
                      sx={{
                        color: "#fff",
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#e5e7eb",
                        },
                        "&:hover .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#3b82f6",
                        },
                        ".MuiSelect-icon": {
                          color: "white",
                        },
                      }}
                    >
                      <MenuItem value="">Select a widget</MenuItem>
                      {props.widgetEditor?.widgets?.map((widget, index) => (
                        <MenuItem
                          key={`${widget.type}-${index}`}
                          value={`${widget.type}-${index}`}
                        >
                          {widget.description
                            ? `${widget.type} (${widget.description})`
                            : widget.type}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
                {widgetFormData && Object.keys(widgetFormData).length > 0 && (
                  <Box sx={{ flex: 1, marginTop: "20px" }}>
                    {renderWidgetFormFields(widgetFormData)}
                  </Box>
                )}
              </Box>
            </Box>
            <Box
              sx={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                gap: "10px",
              }}
            >
              <Tabs
                value={activeTab}
                onChange={handleTabChange}
                sx={{
                  "& .MuiTab-root": {
                    color: "#fff",
                    "&:hover": { backgroundColor: "#3b82f6" },
                  },
                  "& .Mui-selected": {
                    color: "#fff",
                    backgroundColor: "#1e40af",
                  },
                  "& .MuiTabs-indicator": {
                    backgroundColor: "#3b82f6",
                  },
                }}
              >
                <Tab label="Display Preview" value="display" />
                <Tab label="JSON Preview" value="json" />
              </Tabs>
              <Box
                sx={{
                  overflowY: "auto",
                  maxHeight: "600px",
                  paddingRight: "10px",
                  flex: 1,
                }}
              >
                {activeTab === "display" ? (
                  <Box
                    sx={{
                      width: "100%",
                      padding: "10px",
                      border: "2px solid #e5e7eb",
                      borderRadius: "6px",
                      backgroundColor: "#1a1c38",
                      color: "#fff",
                      height: "100%",
                      boxSizing: "border-box",
                      display: "flex",
                      overflowY: "auto",
                    }}
                  >
                    {widgetFormData.type === "carousel" ? (
                      <RichContentCarousel content={widgetFormData} />
                    ) : ["form", "simple_form"].includes(
                        widgetFormData.type
                      ) ? (
                      <SimpleForm data={widgetFormData} />
                    ) : widgetFormData.type === "item_list" ? (
                      <>
                        <ItemList
                          items={widgetFormData}
                          sendMessage={() => {
                            console.log("Send from ItemList");
                          }}
                          onScrollToBottom={() => {
                            console.log("Send from ItemList");
                          }}
                          onOrderSubmit={(order) => {
                            console.log("Send from ItemList");
                          }}
                          isMobile={false}
                        />
                      </>
                    ) : ["text", "send_message"].includes(
                        widgetFormData.type
                      ) ? (
                      <ChatBubble content={widgetFormData} />
                    ) : widgetFormData.type === "image" ? (
                      <>
                        <RichContentImage content={widgetFormData} />
                      </>
                    ) : widgetFormData.type === "table" &&
                      widgetFormData.headers &&
                      Array.isArray(widgetFormData.headers) &&
                      widgetFormData.rows &&
                      Array.isArray(widgetFormData.rows) ? (
                      <TableNew
                        data={widgetFormData}
                        foregroundColor="#fff"
                        backgroundColor="#1a1c38"
                        backgroundColorLight="#202345"
                        metadata={widgetFormData.metadata || {}}
                        scrollToBottom={null}
                      />
                    ) : widgetFormData.type === "form_editor" &&
                      widgetFormData.configObj &&
                      Array.isArray(widgetFormData.configObj.fields) ? (
                      <FormEditor
                        data={widgetFormData}
                        formState={false}
                        onSave={() => {}}
                        metadata={widgetFormData.metadata || {}}
                        foregroundColor="#fff"
                        backgroundColor="#1a1c38"
                        backgroundColorLight="#202345"
                        userId={null}
                        chatUserEmail=""
                        chatUserName=""
                        apiVersion=""
                        agentId={null}
                        showInModal={false}
                        scrollToBottom={null}
                        formSubmissionResponse={null}
                      />
                    ) : widgetFormData.type === "picture_list" &&
                      widgetFormData.options &&
                      Array.isArray(widgetFormData.options) ? (
                      <RichContentPictureList
                        content={widgetFormData}
                        index={0}
                        scrollToBottom={null}
                      />
                    ) : widgetFormData.type === "list" &&
                      widgetFormData.options &&
                      Array.isArray(widgetFormData.options) ? (
                      <RichContentList
                        content={widgetFormData}
                        index={0}
                        scrollToBottom={null}
                        textColor="#fff"
                      />
                    ) : widgetFormData.type === "info" &&
                      widgetFormData.image?.src?.rawUrl ? (
                      <RichContentInfo
                        content={widgetFormData}
                        index={0}
                        scrollToBottom={null}
                      />
                    ) : (
                      <Box
                        sx={{
                          textAlign: "center",
                          height: "100%",
                          display: "flex",
                          flexDirection: "column",
                          justifyContent: "center",
                          alignItems: "center",
                        }}
                      >
                        <h3>Display Preview</h3>
                        <p>
                          Placeholder for {widgetFormData.type || "Widget"}{" "}
                          Component
                        </p>
                        <p>
                          {widgetFormData.type === "table"
                            ? "Invalid or missing table data (headers or rows)."
                            : "This will render the actual component based on the JSON configuration."}
                        </p>
                      </Box>
                    )}
                  </Box>
                ) : (
                  <TextareaAutosize
                    minRows={10}
                    maxRows={17}
                    value={widgetJson}
                    onChange={handleJsonChange}
                    style={{
                      width: "100%",
                      padding: "10px",
                      border: "2px solid #e5e7eb",
                      borderRadius: "6px",
                      backgroundColor: "#1a1c38",
                      color: "#fff",
                      fontFamily: "monospace",
                      resize: "vertical",
                      boxSizing: "border-box",
                    }}
                    onResize={() => {}}
                  />
                )}
              </Box>
            </Box>
          </Box>
          <Box
            sx={{
              display: "flex",
              gap: "10px",
              justifyContent: "flex-end",
              paddingTop: "10px",
              borderTop: "1px solid #e5e7eb",
            }}
          >
            <Button
              onClick={handleSaveWidget}
              variant="contained"
              sx={{
                background: "linear-gradient(to right, #3b82f6, #1e40af)",
                color: "#fff",
              }}
            >
              Save
            </Button>
            <Button
              onClick={() => setWidgetEditorOpen(false)}
              variant="outlined"
              sx={{
                borderColor: "#e5e7eb",
                color: "#fff",
                "&:hover": { borderColor: "#3b82f6" },
              }}
            >
              Cancel
            </Button>
          </Box>
        </Box>
      </Modal>

      {/* Vik(May 9, 2025): Main list display with drag-and-drop Necessary to render the list with reordering and editing capabilities */}
      {items.length > 0 && (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleOnDragEnd}
        >
          <SortableContext
            items={items.map((item, index) =>
              (
                item[props.list_display_field] ||
                item[fields[0]] ||
                index
              ).toString()
            )}
            strategy={verticalListSortingStrategy}
          >
            <div
              style={{
                overflowX: "auto",
                maxWidth: "100%",
              }}
            >
              <table
                style={{
                  width: "100%",
                  maxWidth: "800px",
                  borderCollapse: "separate",
                  borderRadius: "10px",
                  marginTop: "20px",
                  border: "2px solid #1E2A44",
                  borderSpacing: 0,
                  overflow: "hidden",
                }}
              >
                <thead>
                  <tr
                    style={{
                      backgroundColor: "#4D35FF",
                      color: "#FFFFFF",
                      borderBottom: "2px solid #FFFFFF",
                    }}
                  >
                    {getDynamicTableHeaders()}
                  </tr>
                </thead>
                <tbody style={{ backgroundColor: "#202345" }}>
                  {(showAllRows ? items : items.slice(0, 3)).map(
                    (item, index) => (
                      <SortableRow
                        key={
                          item[props.list_display_field] ||
                          item[fields[0]] ||
                          index
                        }
                        id={(
                          item[props.list_display_field] ||
                          item[fields[0]] ||
                          index
                        ).toString()}
                        item={item}
                        index={index}
                        editIndex={editIndex}
                        fields={fields}
                        props={props}
                        formData={formData}
                        handleInputChange={handleInputChange}
                        handleEdit={handleEdit}
                        handleCancel={handleCancel}
                        getDynamicTableBodyCells={getDynamicTableBodyCells}
                        toggleExpandCell={toggleExpandCell}
                        expandedCells={expandedCells}
                        handleOpenWidgetEditor={handleOpenWidgetEditor}
                        showEditInput={showEditInput}
                        getFieldHeaders={getFieldHeaders}
                        getInputFields={getInputFields}
                      />
                    )
                  )}
                </tbody>
              </table>
              {items.length > 3 && (
                <div
                  style={{
                    marginTop: "10px",
                    display: "flex",
                    justifyContent: "start",
                  }}
                >
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowAllRows(!showAllRows);
                    }}
                    type="button"
                    className="primary-gradient-btn submit-btn"
                    style={{
                      padding: "5px 15px",
                      background: "linear-gradient(to right, #3b82f6, #1e40af)",
                      color: "#fff",
                      border: "none",
                      borderRadius: "20px",
                      cursor: "pointer",
                    }}
                  >
                    {showAllRows ? "See Less" : "See More"}
                  </button>
                </div>
              )}
            </div>
          </SortableContext>
        </DndContext>
      )}
    </div>
  );
}

export default List;
